import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Truck, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useLoadDetail } from "@/hooks/useLoadDetail";
import { useAuth } from "@/contexts/AuthContext";
import { deriveStages } from "@/lib/lrDirect/deriveStages";
import { LoadStageTracker } from "@/components/lr-direct/LoadStageTracker";
import { LoadStageCTABar, type CTARole } from "@/components/lr-direct/LoadStageCTABar";
import { editStageTimeDynamic } from "@/hooks/useUpdateDispatchStatus";
import {
  markStageDone, undoMilestoneCascade, closeLoadManual, reopenLoad,
} from "@/lib/lrDirect/canonicalMilestones";
import { StagePopover } from "@/components/lr-direct/StagePopover";
import { DispatchETACards } from "@/components/lr-direct/DispatchETACards";
import { DispatchTrackingLinkCard } from "@/components/lr-direct/DispatchTrackingLinkCard";
import { DispatchLoadNotesCard } from "@/components/lr-direct/DispatchLoadNotesCard";
import { DispatchDocumentsPanel } from "@/components/lr-direct/DispatchDocumentsPanel";
import { DispatchSheet } from "@/components/lr-direct/DispatchSheet";
import { ShipperAwardedBrokerCard } from "@/components/lr-direct/load-detail/ShipperAwardedBrokerCard";
import { ShipperLoadStatusView } from "@/components/lr-direct/ShipperLoadStatusView";



import { useChatSystem } from "@/hooks/useChatSystem";
import { OverviewTab } from "@/components/lr-direct/load-detail/OverviewTab";
import { BidsTab } from "@/components/lr-direct/load-detail/BidsTab";
import { DispatchTab } from "@/components/lr-direct/load-detail/DispatchTab";
import { DocumentsTab } from "@/components/lr-direct/load-detail/DocumentsTab";
import { TimelineTab } from "@/components/lr-direct/load-detail/TimelineTab";
import { MessagesTab } from "@/components/lr-direct/load-detail/MessagesTab";
import { LoadDetailTabsNav } from "@/components/lr-direct/load-detail/LoadDetailTabsNav";
import { useChatContext } from "@/contexts/ChatContext";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";

function fmtEquipment(t: string | null | undefined) {
  if (!t) return "—";
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  const p = parseYYYYMMDDToLocalDate(d);
  return p ? p.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : d;
}

const VALID_TABS = ["overview", "bids", "dispatch", "documents", "timeline", "messages"] as const;
type TabKey = (typeof VALID_TABS)[number];

export default function LRDirectLoadDetailPage() {
  usePageMeta({
    title: "Load Detail | LaneRunner LR Direct",
    description: "Track LR Direct load progress, bids, dispatch, documents, timeline, and messages.",
  });

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useLoadDetail(id);
  const { user } = useAuth();
  const { openChatWithUser } = useChatSystem();

  

  const stages = useMemo(() => {
    if (!data) return [];
    return deriveStages({
      load: data.load,
      dispatch: data.dispatch,
      stops: data.dispatchStops as any[],
      events: data.events,
      podDoc: data.documents.find((d) => d.doc_type === "pod") ?? null,
    });
  }, [data]);

  // Coarse mapping of dynamic stage keys → legacy keys the CTA bar recognises.
  const coarseCurrentStage = useMemo<any>(() => {
    const s = stages.find((x) => x.status === "current")?.key;
    if (!s) return null;
    if (s.startsWith("pickup_") && s.endsWith("_checked_in")) return "truck_assigned";
    if (s.startsWith("pickup_") && s.endsWith("_loaded")) return "picked_up";
    if (s.startsWith("delivery_") && s.endsWith("_checked_in")) return "in_transit";
    if (s.startsWith("delivery_") && s.endsWith("_delivered")) return "delivered";
    if (s === "truck_en_route") return "truck_assigned";
    if (s === "in_transit") return "in_transit";
    return s;
  }, [stages]);


  const role: CTARole | null = useMemo(() => {
    if (!user || !data?.load) return null;
    if (data.load.created_by === user.id) return "shipper";
    if (data.awardedBroker?.user_id && data.awardedBroker.user_id === user.id) return "broker";
    return null;
  }, [user, data]);

  const hasPod = !!data?.documents.find((d) => d.doc_type === "pod");
  const hasDispatch = !!data?.dispatch;
  /** Closed loads block direct milestone undo — Reopen Load first. */
  const isClosedLoad = String((data?.dispatch as any)?.status ?? "") === "closed";
  /** Canonical acceptance signal (legacy status_assigned is read-only elsewhere). */
  const brokerAccepted = (data?.events ?? []).some(
    (e: any) => e.event_type === "broker_accepted",
  );


  // Awarded amount from the winning bid (display only).
  const awardedAmount = useMemo<number | null>(() => {
    const bidId = data?.load?.awarded_bid_id;
    if (!bidId) return null;
    const b = data?.bids.find((x: any) => x.id === bidId);
    return b?.bid_amount != null ? Number(b.bid_amount) : null;
  }, [data?.load?.awarded_bid_id, data?.bids]);

  // Stops for the formatted dispatch sheet: dispatch stops when they exist,
  // otherwise the load's own stops, with origin/destination fallbacks.
  const sheetStops = useMemo(() => {
    const src = (data?.dispatchStops?.length ? data.dispatchStops : data?.loadStops) ?? [];
    const l = data?.load;
    return src.map((s: any) => {
      const isFirstPickup = s.stop_type === "pickup" && s.stop_number === 1;
      const isDelivery = s.stop_type === "delivery";
      return {
        id: s.id,
        stop_number: s.stop_number,
        stop_type: s.stop_type,
        name: s.name ?? (isFirstPickup ? l?.origin_name : isDelivery ? l?.destination_name : null) ?? null,
        address: s.address ?? (isFirstPickup ? l?.origin_address : isDelivery ? l?.destination_address : null) ?? null,
        city: s.city ?? (isFirstPickup ? l?.origin_city : isDelivery ? l?.destination_city : null) ?? null,
        state: s.state ?? (isFirstPickup ? l?.origin_state : isDelivery ? l?.destination_state : null) ?? null,
        postal_code: s.postal_code ?? (isFirstPickup ? l?.origin_postal_code : isDelivery ? l?.destination_postal_code : null) ?? null,
        scheduled_date: s.scheduled_date ?? (isFirstPickup ? l?.pickup_date : isDelivery ? l?.delivery_date : null) ?? null,
        scheduled_time_start: s.scheduled_time_start ?? null,
        scheduled_time_end: s.scheduled_time_end ?? null,
        instructions: s.instructions ?? null,
        contact_name: s.contact_name ?? null,
        contact_phone: s.contact_phone ?? null,
        contact_email: s.contact_email ?? null,
        pickup_number: s.pickup_number ?? null,
        reference_number: s.reference_number ?? null,
        pallet_count: s.pallet_count ?? null,
        confidential_notes: s.confidential_notes ?? null,
      };
    });
  }, [data?.dispatchStops, data?.loadStops, data?.load]);

  const tabParam = searchParams.get("tab");
  // Pre-award loads with bids land on Bids — an auto-created draft dispatch must
  // never bury the live bids the shipper needs to act on. Explicit ?tab= wins.
  const isPreAward = !!data?.load && !data.load.awarded_bid_id;
  const defaultTab: TabKey = isPreAward && (data?.bids.length ?? 0) > 0 ? "bids" : "overview";
  const activeTab: TabKey = (VALID_TABS.includes(tabParam as TabKey) ? tabParam : defaultTab) as TabKey;

  // ---- Pre-award owner → bid room ---------------------------------------
  // This tab page is the POST-AWARD tracker (dispatch / documents / timeline).
  // While the load is still taking bids, the owner belongs in the Live Load
  // Room, which is the single bid + award surface. Catches stale bookmarks,
  // notification links and any remaining inbound links, without touching the
  // broker's own pages (the bid room is owner-only and brokers can't reach
  // this shipper-gated route anyway).
  const isOwner = !!user && !!data?.load && data.load.created_by === user.id;
  useEffect(() => {
    if (isOwner && isPreAward && id) {
      navigate(`/lr-direct/live/${id}`, { replace: true });
    }
  }, [isOwner, isPreAward, id, navigate]);

  const setTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  // ---- Real badge counts -------------------------------------------------
  // Bids: direct_bids rows for this load. Documents: direct_dispatch_documents
  // for this dispatch. Messages: unread messages on conversations threaded to
  // this load (ChatContext).
  const { conversations } = useChatContext();
  const unreadForLoad = useMemo(() => {
    if (!id) return 0;
    return conversations
      .filter((c) => c.loadId === id)
      .reduce((n, c) => n + (c.unreadCount || 0), 0);
  }, [conversations, id]);

  const tabCounts = useMemo(
    () => ({
      bids: data?.bids.length ?? 0,
      documents: data?.documents.length ?? 0,
      messages: unreadForLoad,
    }),
    [data?.bids.length, data?.documents.length, unreadForLoad]
  );

  // ---- Default tab on open ---------------------------------------------
  // Overview is always the landing tab. An explicit, valid ?tab= in the URL
  // still wins (deep links), but nothing auto-selects based on load status —
  // badges highlight activity without hijacking where the user lands.



  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse h-8 w-40 bg-muted rounded mb-4" />
          <div className="animate-pulse h-32 bg-muted rounded mb-4" />
          <div className="animate-pulse h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }
  if (error || !data?.load) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm font-medium mb-1">Load not found</p>
          <p className="text-xs text-muted-foreground mb-3">It may have been deleted or you don't have access.</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/lr-direct/my-loads?stage=all", { replace: true })}>
            Back to My Loads
          </Button>
        </div>
      </div>
    );
  }

  const load = data.load;

  // Formatted dispatch sheet — rendered inside the Overview and Dispatch tabs only.
  // The dispatch sheet only belongs on Overview once the packet is real: the
  // load is awarded, or the shipper explicitly marked the packet prepared. A
  // draft dispatch row is auto-created shortly after posting, so gating on the
  // row's existence alone made pre-award loads look like finished dispatches.
  const showDispatchSheetOnOverview =
    !!data?.dispatch && (!!data?.load?.awarded_bid_id || !!(data.dispatch as any).dispatch_prepared_at);

  const dispatchSheet = (
        <div className="rounded-lg border border-border/70 shadow-sm overflow-hidden bg-white">

          <DispatchSheet
            fill
            load={{
              load_number: load.load_number,
              origin_city: load.origin_city,
              origin_state: load.origin_state,
              destination_city: load.destination_city,
              destination_state: load.destination_state,
              equipment_type: load.equipment_type,
              pickup_date: load.pickup_date,
              delivery_date: load.delivery_date ?? null,
              commodity: load.commodity ?? null,
              weight_lbs: load.weight_lbs != null ? Number(load.weight_lbs) : null,
              length_ft: load.length_ft ?? null,
              total_miles: load.total_miles != null ? Number(load.total_miles) : null,
              special_instructions: load.special_instructions ?? null,
              shipper_notes_to_brokers: load.shipper_notes_to_brokers ?? null,
              awarded_at: load.awarded_at ?? null,
            }}
            stops={sheetStops}
            broker={
              data.awardedBroker?.company_name
                ? {
                    company_name: data.awardedBroker.company_name,
                    contact_name: data.awardedBroker.contact_name ?? "",
                    email: data.awardedBroker.email ?? null,
                    phone: data.awardedBroker.phone ?? null,
                  }
                : null
            }
            awardedAmount={awardedAmount}
            totalMiles={load.total_miles != null ? Number(load.total_miles) : null}
          />
        </div>
  );

  // ---- Shipper monitoring view (awarded loads, Overview tab) --------------
  // Read-only milestone monitoring: same canonical `stages` the broker view
  // uses, no advance/undo controls. Notes, documents and messaging reuse the
  // existing shared flows.
  if (role === "shipper" && load.awarded_bid_id) {
    // Every tab renders inside the same shell: chrome above the tab bar never
    // changes, only this pane swaps. Legacy tab components keep their existing
    // functionality; the shipper-scoped `.shipper-tab-pane` styling in
    // index.css aligns them with the new visual system without touching the
    // broker view, which renders the same components unwrapped.
    const shipperTabPane: React.ReactNode =
      activeTab === "overview" ? null
      : activeTab === "bids" ? <BidsTab load={load} bids={data.bids} />
      : activeTab === "dispatch" ? (
          <DispatchTab loadId={load.id} dispatch={data.dispatch} dispatchStops={data.dispatchStops} sheet={dispatchSheet} />
        )
      : activeTab === "documents" ? (
          data.dispatch?.id && user ? (
            <DispatchDocumentsPanel
              dispatchId={data.dispatch.id}
              loadId={load.id}
              uploaderId={user.id}
              counterpartyUserId={data.awardedBroker?.user_id ?? null}
              canUpload
              shipperUserId={load.created_by}
              cardClassName="border-border bg-card shadow-sm"
              onChanged={() => refetch()}
            />
          ) : (
            <DocumentsTab loadId={load.id} documents={data.documents} dispatchExists={!!data.dispatch} />
          )
        )
      : activeTab === "timeline" ? <TimelineTab load={load} dispatch={data.dispatch} events={data.events} />
      : activeTab === "messages" ? <MessagesTab loadId={load.id} awardedBroker={data.awardedBroker} />
      : null;

    return (
      <ShipperLoadStatusView
        load={{
          id: load.id,
          load_number: load.load_number,
          origin_city: load.origin_city,
          origin_state: load.origin_state,
          destination_city: load.destination_city,
          destination_state: load.destination_state,
          total_miles: load.total_miles != null ? Number(load.total_miles) : null,
          awarded_at: load.awarded_at ?? null,
          created_by: load.created_by,
        }}
        stages={stages}
        awardedBroker={data.awardedBroker as any}
        awardedAmount={awardedAmount}
        dispatch={data.dispatch as any}
        viewerUserId={user!.id}
        activeTab={activeTab}
        onTabChange={setTab}
        tabCounts={tabCounts}
        onBack={() => navigate("/lr-direct/my-loads?stage=all", { replace: true })}
        onRefresh={() => refetch()}
        onMessageBroker={
          data.awardedBroker?.user_id
            ? () =>
                openChatWithUser(
                  data.awardedBroker!.user_id!,
                  data.awardedBroker!.company_name || data.awardedBroker!.contact_name || "Broker",
                  undefined,
                  load.id,
                )
            : undefined
        }
        onOpenDispatchSheet={hasDispatch ? () => navigate(`/lr-direct/dispatch/${load.id}`) : undefined}
      >
        {shipperTabPane}
      </ShipperLoadStatusView>
    );
  }


  return (

    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-3 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-2 min-w-0">
            {/* Back always lands on the full My Loads list ("All"), never a filtered
                stage tab the shipper may have passed through. */}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" onClick={() => navigate("/lr-direct/my-loads?stage=all", { replace: true })}>

              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{load.load_number}</span>
                {load.priority_tag && (
                  <Badge variant="outline" className="text-[10px] uppercase">{load.priority_tag}</Badge>
                )}
              </div>
              <h1 className="text-lg md:text-xl font-bold text-foreground flex items-center gap-1.5 mt-0.5">
                <MapPin className="h-4 w-4 text-brand-teal" />
                <span className="truncate">
                  {load.origin_city}, {load.origin_state}
                  <span className="text-muted-foreground mx-1.5">→</span>
                  {load.destination_city}, {load.destination_state}
                </span>
              </h1>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{fmtEquipment(load.equipment_type)}</span>
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Pickup {fmtDate(load.pickup_date)}</span>
                {load.total_miles && <span className="tabular-nums">{Math.round(load.total_miles).toLocaleString()} mi</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" />
        </div>

        {/* TOP BOX — awarded loads show the Awarded Broker & Rate card (matches
            the broker page's award banner). Pre-award keeps the CTA bar. */}
        {role === "shipper" && load.awarded_bid_id && (
          <ShipperAwardedBrokerCard
            loadId={load.id}
            awardedBroker={data.awardedBroker}
            awardedAmount={awardedAmount}
            miles={load.total_miles ? Math.round(Number(load.total_miles)) : null}
            statusLabel={String(coarseCurrentStage ?? "awarded").replace(/_/g, " ")}
            hasDispatch={hasDispatch}
            onMessageBroker={
              data?.awardedBroker?.user_id
                ? () =>
                    openChatWithUser(
                      data.awardedBroker!.user_id!,
                      data.awardedBroker!.company_name || data.awardedBroker!.contact_name || "Broker",
                      undefined,
                      load.id,
                    )
                : undefined
            }
          />
        )}

        {/* Per-stage CTA action bar (uses coarse legacy key mapping). */}
        {role && !(role === "shipper" && load.awarded_bid_id) && (
          <LoadStageCTABar
            currentStage={coarseCurrentStage}
            role={role}
            loadId={load.id}
            hasDispatch={hasDispatch}
            hasPod={hasPod}
            onUpdateStatus={() => navigate(`/lr-direct/dispatches/build/${load.id}`)}
            onUploadPod={() => setTab("documents")}
            onMessageBroker={
              data?.awardedBroker?.user_id
                ? () =>
                    openChatWithUser(
                      data.awardedBroker!.user_id!,
                      data.awardedBroker!.company_name || data.awardedBroker!.contact_name || "Broker",
                      undefined,
                      load.id,
                    )
                : undefined
            }
            onMarkComplete={async () => {
              if (!data?.dispatch?.id || !user) return;
              try {
                // Canonical manual close — POD is never implied.
                await closeLoadManual({
                  dispatchId: data.dispatch.id,
                  loadId: load.id,
                  actorId: user.id,
                  counterpartyUserId:
                    load.created_by !== user.id ? load.created_by : (data.awardedBroker?.user_id ?? null),
                  hasPod,
                });
                toast.success("Load closed");
                refetch();
              } catch (e: any) {
                toast.error(e?.message || "Failed to close load");
              }
            }}
          />
        )}

        {/* Progress tracker — broker can click circles to set / undo status. */}
        <div className="border border-brand-teal/25 rounded-lg bg-brand-teal/[0.06] shadow-sm p-4 mb-4">
          <LoadStageTracker
            stages={stages}
            renderStageButton={
              role === "broker" && data?.dispatch?.id && user
                ? (stage, defaultButton) => {
                    const isBroker = stage.kind === "broker_stop" || stage.kind === "broker_dispatch";
                    if (!isBroker) return null;
                    const dispatchId = data.dispatch!.id;
                    const counterpartyUserId =
                      load.created_by !== user.id
                        ? load.created_by
                        : (data.awardedBroker?.user_id ?? null);
                    const clickable = (
                      <button
                        type="button"
                        className={defaultButton.props.className + " cursor-pointer hover:ring-2 hover:ring-brand-teal/40 hover:ring-offset-1 transition-shadow"}
                        aria-label={`${stage.label} — click to update`}
                      >
                        {defaultButton.props.children}
                      </button>
                    );
                    return (
                      <StagePopover
                        stage={stage}
                        allStages={stages}
                        closed={isClosedLoad}
                        onRequestReopen={async () => {
                          try {
                            await reopenLoad({
                              dispatchId, loadId: load.id, actorId: user.id, stages,
                              accepted: brokerAccepted, counterpartyUserId,
                            });
                            toast.success("Load reopened — corrections are available again");
                            refetch();
                          } catch (e: any) {
                            toast.error(e?.message || "Failed to reopen load");
                          }
                        }}
                        onMarkDone={async (s) => {
                          await markStageDone({
                            stage: s, dispatchId, loadId: load.id, actorId: user.id,
                            counterpartyUserId, shipperUserId: load.created_by,
                            loadNumber: load.load_number,
                          });
                          toast.success(`${s.label} marked done`);
                          refetch();
                        }}
                        onUndo={async (s) => {
                          await undoMilestoneCascade({
                            stages, target: s, dispatchId, loadId: load.id,
                            actorId: user.id, accepted: brokerAccepted,
                          });
                          toast.success(`${s.label} undone`);
                          refetch();
                        }}
                        onEditTime={async (s, iso) => {
                          await editStageTimeDynamic({ dispatchId, stage: s, newAtIso: iso, actorId: user.id });
                          toast.success("Time updated");
                          refetch();
                        }}
                      >
                        {clickable}
                      </StagePopover>
                    );
                  }
                : undefined
            }
          />
        </div>



        {/* ETA cards — broker editable; shipper sees read-only with "updated Xm ago". */}
        {data?.dispatch?.id && user && (
          <>
            <DispatchETACards
              dispatchId={data.dispatch.id}
              loadId={load.id}
              pickupEtaAt={(data.dispatch as any).pickup_eta_at ?? null}
              pickupEtaUpdatedAt={(data.dispatch as any).pickup_eta_updated_at ?? null}
              deliveryEtaAt={(data.dispatch as any).delivery_eta_at ?? null}
              deliveryEtaUpdatedAt={(data.dispatch as any).delivery_eta_updated_at ?? null}
              editable={role === "broker"}
              actorId={user.id}
              counterpartyUserId={load.created_by !== user.id ? load.created_by : (data.awardedBroker?.user_id ?? null)}
              shipperUserId={load.created_by}
              onChanged={() => refetch()}
              cardClassName="border-lr-yellow/30 bg-lr-yellow/[0.07] shadow-sm"
            />
            <DispatchTrackingLinkCard
              dispatchId={data.dispatch.id}
              loadId={load.id}
              trackingUrl={(data.dispatch as any).tracking_url ?? null}
              trackingUrlUpdatedAt={(data.dispatch as any).tracking_url_updated_at ?? null}
              editable={role === "broker"}
              actorId={user.id}
              counterpartyUserId={load.created_by !== user.id ? load.created_by : (data.awardedBroker?.user_id ?? null)}
              shipperUserId={load.created_by}
              onChanged={() => refetch()}
              cardClassName="border-brand-teal/15 bg-brand-teal/[0.03] shadow-sm"
            />
            {/* Shared shipper/broker note thread — closes the live-status block. */}
            {role && (
              <DispatchLoadNotesCard
                loadId={load.id}
                viewerRole={role === "broker" ? "broker" : "shipper"}
                viewerUserId={user.id}
                fallbackCompanyName={role === "broker" ? data?.awardedBroker?.company_name ?? null : null}
                cardClassName="border-border/70 bg-muted/20 shadow-sm"
              />
            )}
            {/* Load Documents — full width, same shared panel/behavior as the broker page. */}
            <div className="mb-4">
              <DispatchDocumentsPanel
                dispatchId={data.dispatch.id}
                loadId={load.id}
                uploaderId={user.id}
                counterpartyUserId={load.created_by !== user.id ? load.created_by : (data.awardedBroker?.user_id ?? null)}
                canUpload
                shipperUserId={load.created_by}
                cardClassName="border-brand-teal/15 bg-brand-teal/[0.03] shadow-sm"
              />
            </div>
          </>
        )}

        {/* Primary page navigation */}
        <Tabs value={activeTab} onValueChange={setTab}>
          <LoadDetailTabsNav
            value={activeTab}
            onValueChange={(k) => setTab(k)}
            counts={tabCounts}
            alertTabs={["messages"]}
          />


          <TabsContent value="overview" className="mt-4 motion-safe:animate-fade-in">
            <div className="space-y-4">
              {/* Pre-award: surface the bids first so the award path is obvious. */}
              {showDispatchSheetOnOverview ? null : data.bids.length > 0 ? (
                <BidsTab load={load} bids={data.bids} />
              ) : null}
              <OverviewTab load={load} loadStops={data.loadStops} bids={data.bids} awardedBroker={data.awardedBroker} />
              {showDispatchSheetOnOverview ? dispatchSheet : null}
            </div>
          </TabsContent>
          <TabsContent value="bids" className="mt-4 motion-safe:animate-fade-in">
            <BidsTab load={load} bids={data.bids} />
          </TabsContent>
          <TabsContent value="dispatch" className="mt-4 motion-safe:animate-fade-in">
            <DispatchTab loadId={load.id} dispatch={data.dispatch} dispatchStops={data.dispatchStops} sheet={dispatchSheet} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4 motion-safe:animate-fade-in">
            {data.dispatch?.id && user ? (
              <DispatchDocumentsPanel
                dispatchId={data.dispatch.id}
                loadId={load.id}
                uploaderId={user.id}
                counterpartyUserId={load.created_by !== user.id ? load.created_by : (data.awardedBroker?.user_id ?? null)}
                canUpload
                shipperUserId={load.created_by}
                cardClassName="border-brand-teal/15 bg-brand-teal/[0.03] shadow-sm"
              />
            ) : (
              <DocumentsTab loadId={load.id} documents={data.documents} dispatchExists={!!data.dispatch} />
            )}
          </TabsContent>
          <TabsContent value="timeline" className="mt-4 motion-safe:animate-fade-in">
            <TimelineTab load={load} dispatch={data.dispatch} events={data.events} />
          </TabsContent>
          <TabsContent value="messages" className="mt-4 motion-safe:animate-fade-in">
            <MessagesTab loadId={load.id} awardedBroker={data.awardedBroker} />
          </TabsContent>
        </Tabs>


      </div>
    </div>
  );
}
